import { Observable, Subject } from 'rxjs'
import { ReactNode } from 'react'
import CRptContentAlarmSettingsUpdate from '../../Alarms/CRptContentAlarmSettingsUpdate'
import { MainMenuApp, MainMenuAppTransfer } from '../../constants/AppMenuConstants'
import { DashboardContentUpdate } from '../../Types/Types'
import { SidebarMenuItems } from '../../constants/ReactRouter/Header/Constants'
import { IconName } from '../../constants/React/Icon/Constants'
import { ResultType } from '../API/REST/RESTAPIError'
import { RESTMethods } from '../../constants/REST'
import { OptionsObject } from '../../../Vendor/notistack'
import { StatusCodes } from '../../constants/StatusCodes'
import { CxiDataEntitlement, CxIntegrationLicence } from './SelfProvisioning/SelfProvisioningTypes'
import { ApplicationUser } from './AddUser/AddUserTypes'
import { CRMConfigurationTemplateType } from '../../../ReactRouting/Components/Organisms/CRMConfiguration/Modal/Constants/Constants'

export type CRMTypesResponse = {
    crmType: string
    crmTypeName: string
}

export type AlarmSettings = {
    alarmSettings?: CRptContentAlarmSettingsUpdate[]
    reportID?: string
    mediaFolder?: string
}

export type RequestCacheOptions = {
    ttl?: number
}

export type AdminUserBody = {
    Password?: string
    EmailAddr: string // "service@akixi.com",
    EnableMFA: boolean
    SynchroniseSupervisorPermissions?: boolean
    Advanced: {
        PromptForTerms?: boolean
        ReportUpdInterval: number // 'Auto'
        NotifyPasswordChgs?: boolean
        JavaScriptAuditOutput?: boolean
        DisableMediaPlugIns?: boolean
        DisableDragDropFeatures?: boolean
    }
    /** Undefined = not sent, so the backend keeps the user's stored value (APD-13533). */
    PasswordChgRequired?: boolean
    Endpoint: {
        TelSysID: Nullable<number> // '10'
        AgentNumber: Nullable<string> // 'None'
        DeviceNumber: Nullable<string> // '105'
        PartIDInTelSys: Nullable<number> // '2'
    }
    Username: string // 'Admin'
    UsernameNew?: string // 'Admin'
    DefaultLang: string // 'en_US'
    FullName: string // 'vi to'
    TimeZone: Nullable<string>
    Scope: {
        CMEnvironmentID: string
        CallRecIntID: string // 'All'
        TelSysID: number[] // 'All'
        /**
         * Whether the user holds administrative rights. Omitting it on an update clears them, so a
         * caller that rebuilds the scope must carry the stored value over (APD-13568).
         */
        Admin?: boolean
        ReportReadOnly?: boolean
        ReportingAccess: string // 'Akixi 2000'
        OmniChannelACDAgent?: boolean
        AgentNumber: string // 'All'
        DeviceNumber: string // 'All'
        ServiceNotify?: boolean
        EnhancedMobileReporting?: boolean
        ConcurrentSignIns?: boolean
        PartIDInTelSys: number[] // 'All'
        ReportDataAPI?: boolean
        DefaultReportCollection?: Nullable<string>
        // Legacy CXI User field kept for backward compatibility while callers migrate to explicit CXI User/CXI Data fields.
        CXILicenseType?: CxIntegrationLicence
        CXScoreLicenseType?: string
        /** Device-level CX Score monitoring flag, applied via the user API (APD-12994). Null = not set by the user modal. */
        CXScoreEnabled?: Nullable<boolean>
        CXRLicenseType?: string
        CXRecordEnabled?: Nullable<boolean>
        // New additive CXI Data entitlement field.
        CXIDataEntitlement?: CxiDataEntitlement
    }
    Permissions: {
        Admin: string[]
        NonAdmin: string[]
        Other: string[]
    }
    LockOut: boolean
    SendWelcomeEmail: boolean
    SendAuthEmail?: boolean
}

export type ApplicationUserResponse = APIData<ApplicationUser>

export type User = {
    Username: string
    FullName: string
    EmailAddr?: string
}

export type APIMapper<T extends unknown> = (data: JSONResponse<T>) => JSONResponse<T>

export interface APIListData<T extends unknown> {
    Data: {
        Items: T[]
    }
}

export interface APIListDataWithTotal<T extends unknown> {
    Data: {
        Items: T[]
        Total: number
        Offset: number
    }
}

export type ActiveRpt = {
    deviceID?: string
    callID?: string
}

export type CallDetailsInfo = {
    fallbackID?: string
    itemID?: string
    itemIDNum?: number
    meetingID?: string
    start?: string
    end?: string
}

export interface ReportRowDataType {
    rowID?: string
    reportType?: number

    telSysID?: string
    partitionID?: string
    deviceID?: string
    agentID?: string
    callID?: string

    deviceCallIDActive?: string
    callIDTelSysTarget?: string

    callCtrlFlags?: number
    deviceCallCtrlFlags?: number

    isActiveReport?: boolean
    isRealTime?: boolean
    enableNonUserEndpointActions?: boolean
    isDNDAndACDNAShown?: boolean
    isSearchInfoIDMain?: boolean
    isSearchInfoIDAlt?: boolean
    isSearchInfoIDItem?: boolean
    isCallActive?: boolean
    currentUserMode?: boolean

    activeReport?: ActiveRpt
    groupsFilter?: string[]

    callDetails?: CallDetailsInfo

    updateState?: () => void
}

export type ResponsePassThrough<T extends unknown> = {
    status?: number
    response: JSONResponse<T>
    timedOut?: boolean
}

export type AkixiCom = {
    Akixi: Record<string, unknown>
}

export interface ModifyRESTOptions<T extends Nullable<object>> extends StandardRESTOptions {
    method: RESTMethods.POST | RESTMethods.PATCH | RESTMethods.PUT | RESTMethods.DELETE
    body?: T
    cacheTime?: undefined
}
export interface ModifyRESTOptionsWithCache<T extends Nullable<object>> extends StandardRESTOptions {
    method: RESTMethods.POST | RESTMethods.PATCH | RESTMethods.PUT | RESTMethods.DELETE
    __INTENTIONALLY_CACHE_NON_GET__: true
    body?: T
    cacheTime?: number
}

export type StandardRESTOptions = {
    headers?: object
    errorOnNoContent?: boolean
    additionalAllowableNoContentCodes?: StatusCodes[]
    isStringResult?: boolean
    timeout?: number
}

export type SnackBarFunction = (message: ReactNode, options?: OptionsObject | undefined) => React.ReactText

export interface GETOptions extends StandardRESTOptions {
    method: RESTMethods.GET
    body?: undefined
    cacheTime?: number
}

export type ReportListUpdateObserverSubscribe = (
    subscriberID: string,
    subscriberCallback: (reportID?: string, reportDesc?: string) => Promise<void>
) => void

export type ReportListUpdateObserverUnsubscribe = (subscriberID: string) => void

export type ReportListUpdateObserver = {
    subscribers: Map<unknown, (reportID?: string, reportDesc?: string) => Promise<void>>
    notifySubscribers: (reportID: string, reportDesc: string) => void
    subscribe: ReportListUpdateObserverSubscribe
    unsubscribe: ReportListUpdateObserverUnsubscribe
}

// Notification Types
export type ApplicationResponseNotification = {
    UserID: string
    DoNotDisturb: boolean
    NotificationsTelSysID: Nullable<number>
    NotificationsPartitionIDInTelSys: Nullable<number[]>
    NotificationsDeviceNumber: Nullable<string>
    TelephoneNumber: Nullable<string>
    NotificationChannels: string[]
    UserNotifications: UserNotificationsType[]
    DarkMode: boolean
    Favourites: SidebarMenuItems[]
    TimeZone?: Nullable<string>
    FusionPortalMode: boolean
    FusionLegacyMode: boolean
}

export type UserPagePermissions = {
    reporting: {
        reports: boolean
        insights: boolean
        presence: boolean
    }
    fastProvisioning: {
        userManagement: boolean
        queueManagement: boolean
        selfProvisioning: boolean
    }
}

export interface APIData<T extends unknown> {
    Data: T
}

export type CurrentUserResponse = APIData<AdminUserResponse>

export type UserPermissions = {
    CallRecIntID: string // 'All'
    CMEnvironmentID: string // UUID
    TelSysID: number[] // 'All'
    TelSysTypes: string[]
    ReportingAccess: string // 'Akixi 2000'
    OmniChannelACDAgent: boolean
    AgentNumber: string // 'All'
    IsDeviceTypeGroup: boolean
    DeviceNumber: string // 'All'
    ServiceNotify: boolean
    EnhancedMobileReporting: boolean
    ConcurrentSignIns: boolean
    Admin: boolean
    PartIDInTelSys: number[] // 'All'
    ReportDataAPI: boolean
    ReportReadOnly: boolean
    CxiLicenceType: CxIntegrationLicence
    CXIDataEntitlement?: CxiDataEntitlement
}

export type InternalUserResponse = {
    FullName: string
    Username: string
    ReportingAccess?: string
    PendingDeletion?: boolean
}

/**
 * Administration API User data type.
 * All the properties are commented with examples of values as some of them are string-ified versions of other types.
 */
export type AdminUserResponse = {
    Password?: string
    EmailAddr: string // "service@akixi.com",
    CognitoSession?: string
    DisablePresenceBoard?: boolean
    /**
     * Whether the backend permits this user to access the Teams presence board, computed server-side
     * from the TelSys configuration (type, comms enabled, integration type/mode/options) and the user's
     * reporting licence. Mirrors the presence route guard so the icon cannot drift from page access.
     */
    CanAccessPresence?: boolean
    EnableMFA?: boolean
    MFAChallengeName?: MFAChallengeNames
    CognitoAccessToken?: string
    Advanced: {
        PromptForTerms: boolean
        ReportUpdInterval: string // 'Auto'
        NotifyPasswordChgs: boolean
        JavaScriptAuditOutput: boolean
        DisableMediaPlugIns: boolean
        DisableDragDropFeatures: boolean
    }
    PasswordChgRequired: boolean
    Endpoint: {
        TelSysID: number // 10
        AgentNumber: string // 'None'
        DeviceNumber: string // '105'
        PartIDInTelSys: number // 2
    }
    Username: string // 'Admin'
    DefaultLang: string // 'en_US'
    FullName: string // 'vi to'
    Scope: UserPermissions
    Permissions: {
        Admin: string[]
        NonAdmin: string[]
        Other: string[]
    }
    SessionInfo: {
        SignInAsActive: boolean
        Language: string
        PreviewBrandingID?: Nullable<string>
        SignedInWithSSO?: boolean
    }
    LockOut: boolean
    InsightsHubHidden: boolean
    ShowUiVersionToggle: boolean
    TimeZone: Nullable<string>
}

export interface RxJSStateDefinable<T> extends RxJSState<Definable<T>> {
    subject: Subject<Definable<T>>
    observable: Observable<Definable<T>>
    announce: (update?: T) => void
    resolver?: undefined
}

export type RxJSState<T> = {
    subject: Subject<T>
    observable: Observable<T>
    announce: (update: T) => void
    resolver?: undefined
}

export type RxJSStateWithResolver<T, U extends () => void> = {
    subject: Subject<Definable<T>>
    observable: (...args: Parameters<U>) => Observable<Definable<T>>
    announce: (update?: T) => void
    resolver?: U
}

export type RxJSWindow = {
    reportListUpdate: RxJSStateDefinable<ReportListState>
    userInfo?: RxJSStateDefinable<Definable<AdminUserResponse>>
    userInfoError?: RxJSStateDefinable<boolean>
    currentUserState?: RxJSStateWithResolver<
        AdminUserResponse,
        (isCacheBusted?: boolean, errorHandler?: (e: Error) => void) => Promise<Nullable<CurrentUserResponse>>
    >
    disabledFlags?: RxJSStateDefinable<Record<string, Definable<boolean>>>
    localStorageSet?: RxJSState<string>
    subMenuItems?: RxJSState<Record<MainMenuApp, SubMenuItem[]>>
    dashboardContentUpdate?: RxJSStateDefinable<DashboardContentUpdate>
    dragUpdate?: RxJSStateDefinable<Element>
    eptSearch?: RxJSStateDefinable<Nullable<string>>
    newReportCreated?: RxJSStateDefinable<NewReport>
    personalDashboardUpdate?: RxJSStateDefinable<boolean>
    reportingMenuTrigger?: RxJSStateDefinable<boolean>
    reportingMenuFavourites?: RxJSStateDefinable<SidebarMenuItems[]>
    userSettings?: RxJSStateWithResolver<
        ApplicationResponseNotification,
        () => Promise<Nullable<ApplicationResponseNotification>>
    >
    crmTypes?: RxJSStateDefinable<Record<CRMConfigurationTemplateType, CRMTypesResponse[]>>
    alarmingUpdate?: RxJSStateDefinable<Definable<AlarmSettings>>
    mainMenuTrigger?: RxJSStateDefinable<boolean>
    appMenuUpdate?: RxJSStateDefinable<MainMenuAppTransfer>
    userPagePermissions?: RxJSStateDefinable<UserPagePermissions>
    headerSidebarCollapse?: RxJSState<boolean>
}

/**
 * Report list state.
 */
export type ReportListState = {
    ReportID?: string
    ReportDesc?: string
    ReportList?: ReportListItemResponse[]
    isLoading?: boolean
}

export type NewReport = {
    reportID: string
    reportDesc: string
    uuid: string
}

export type SubMenuItem = {
    id: SidebarMenuItems
    title: string
    icon: IconName
    enabled: boolean
    handleClick: (isMiddleClick?: boolean) => void
    active?: boolean
    favourite?: boolean
}

/**
 * Report object type.
 */
export type ReportListItemResponse = {
    ID: string
    ReportID: string
    Duration: number
    ReportType: number
    GroupingViewType: number
    SupportsIconsViews: boolean
    SupportsCharting: boolean
    SupportsRowHighlighting: boolean
    ViewType: number
    WBTileType: number
    FilterDevices: string
    FilterDevicesMax: number
    UserLicensedFor: number
    IsLicensed: boolean
    IsLicensedRealTime: boolean
    IsScheduleActive: boolean
    ZoomLevel: number
    IsAutoSwitched: boolean
    IsBinned: boolean
    AutoSwitchIntervalDisplay: number
    DurationFilterSummary: string
    Description: string
    DaysUntilDeletion: number
}

export enum MFAChallengeNames {
    SOFTWARE_TOKEN_MFA = 'SOFTWARE_TOKEN_MFA'
}

export type UserNotificationsType = {
    Name: string
    NotificationEnabled: boolean
    Threshold: number
}

/**
 * Report list response.
 */
export type ReportListResponse = {
    ReportList: ReportListItemResponse[]
}

export type AkixiWindow = Window &
    typeof globalThis & {
        ReportListUpdateObserver: ReportListUpdateObserver
        // eslint-disable-next-line
        configurationTabs: any
        sSERVLETPATH: string
        snackbar: SnackBarFunction
        m_bCallRecIntegrationEnabled: Definable<boolean>
        rxjs: RxJSWindow
        popoutUUID?: string
        pMainMenuBurger: Element
        pauseReportXMLContentUpdates: () => void
        resumeUpdates: () => void
        disableHotKeys?: boolean
        m_bIsFullscreenActive?: boolean
        AkixiUserAgentID?: number
        firstSubs?: Record<string, boolean>
        com: AkixiCom
    }

export interface RequestGETOptions extends StandardRESTOptions {
    cacheTime?: number
}

export interface ServerError extends Error {
    readonly ErrorCode: number
    readonly ErrorMessage: string
    readonly Message: string
    readonly Result: ResultType
}

export interface ServerErrorCode extends ServerError {
    readonly Code: string
    readonly CodeGUID?: string
}

export type JSONResponse<T extends unknown> = Nullable<T>

export type RESTOptions<T extends Nullable<object>> = GETOptions | ModifyRESTOptions<T> | ModifyRESTOptionsWithCache<T>
